import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CommentIcon from '@mui/icons-material/Comment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import InfiniteScroll from 'react-infinite-scroll-component';
import AssignmentEvent from '../components/AssignmentEvent';
import GroupMatchEvent from '../components/GroupMatchEvent';
import MatchEvent from '../components/MatchEvent';
import SOIMatchEvent from '../components/SOIMatchEvent';
import NoteEvent from '../components/NoteEvent';
import EventListItem from './EventListItem';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

/**
 * Drawer UI component for displaying event history
 * Separated from logic for better maintainability
 */
export default function HistoryDrawer({
  open,
  onClose,
  loading,
  loadingMore,
  hasMore,
  error,
  success,
  clearError,
  clearSuccess,
  topLevelEvents,
  allEvents,
  expandedGroupMatches,
  openAssignments,
  onEventClick,
  onDeleteClick,
  onToggleGroupMatch,
  onToggleAssignmentStatus,
  onOpenNoteDialog,
  onOpenCreateDialog,
  onOpenCompleteDialog,
  wsConnected = false,
  onWSReconnect,
  onRefresh,
  onLoadMore,
  pageSize,
  onPageSizeChange
}) {
  return (
    <Drawer
      sx={{
        width: 300,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 300,
          boxSizing: 'border-box',
        },
      }}
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <DrawerHeader>
        <IconButton onClick={onClose}>
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />

        {/* WebSocket connection indicator */}
        <Tooltip title={wsConnected ? 'WebSocket connected' : 'WebSocket disconnected - Click to reconnect'} arrow>
          <span>
            <IconButton
              size="small"
              onClick={wsConnected ? undefined : onWSReconnect}
              disabled={wsConnected}
              sx={{ marginRight: 1, padding: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: wsConnected ? '#2e7d32' : '#9e9e9e',
                  boxShadow: wsConnected ? '0 0 8px #2e7d32' : 'none',
                  cursor: wsConnected ? 'default' : 'pointer'
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        <IconButton onClick={onRefresh} disabled={loading} title="Refresh events">
          <RefreshIcon />
        </IconButton>
        <IconButton onClick={onOpenNoteDialog} disabled={loading}>
          <CommentIcon />
        </IconButton>
        <IconButton onClick={onOpenCreateDialog} disabled={loading}>
          <AssignmentIcon />
        </IconButton>
        <IconButton
          onClick={onOpenCompleteDialog}
          disabled={loading || openAssignments.length === 0}
        >
          <AssignmentTurnedInIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={clearSuccess} sx={{ mb: 1 }}>
            {success}
          </Alert>
        )}

        {loading && topLevelEvents.length === 0 && (
          <List dense>
            {[1, 2, 3, 4, 5].map(i => (
              <Box key={i} sx={{ mb: 1 }}>
                <Skeleton variant="rectangular" height={60} />
              </Box>
            ))}
          </List>
        )}

        {topLevelEvents.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary">
            No events
          </Typography>
        )}

        {topLevelEvents.length > 0 && (
          <>
            <div id="scrollableDiv" style={{ flex: 1, overflow: 'auto' }}>
              <InfiniteScroll
                dataLength={topLevelEvents.length}
                next={onLoadMore}
                hasMore={hasMore && !loadingMore}
                loader={
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                }
                endMessage={
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                    No more events
                  </Typography>
                }
                scrollableTarget="scrollableDiv"
              >
                <List dense>
                  {topLevelEvents.map((event) => {
                    // Each component renders itself and its children
                    if (event.eventType === 'assignment') {
                      return (
                        <AssignmentEvent
                          key={event.eventId}
                          assignment={event}
                          allEvents={allEvents}
                          expandedGroupMatches={expandedGroupMatches}
                          onToggleGroupMatch={onToggleGroupMatch}
                          onToggleAssignmentStatus={onToggleAssignmentStatus}
                          onEventClick={onEventClick}
                          onDeleteClick={onDeleteClick}
                          disabled={loading}
                        />
                      );
                    }

                    if (event.eventType === 'group_match') {
                      return (
                        <GroupMatchEvent
                          key={event.eventId}
                          groupMatch={event}
                          allEvents={allEvents}
                          expandedGroupMatches={expandedGroupMatches}
                          onToggleGroupMatch={onToggleGroupMatch}
                          onEventClick={onEventClick}
                          onDeleteClick={onDeleteClick}
                          disabled={loading}
                        />
                      );
                    }

                    if (event.eventType === 'match') {
                      return (
                        <MatchEvent
                          key={event.eventId}
                          match={event}
                          onEventClick={onEventClick}
                          onDeleteClick={onDeleteClick}
                          disabled={loading}
                        />
                      );
                    }

                    if (event.eventType === 'soi_match') {
                      return (
                        <SOIMatchEvent
                          key={event.eventId}
                          match={event}
                          onEventClick={onEventClick}
                          onDeleteClick={onDeleteClick}
                          disabled={loading}
                        />
                      );
                    }

                    if (event.eventType === 'note') {
                      return (
                        <NoteEvent
                          key={event.eventId}
                          note={event}
                          onEventClick={onEventClick}
                          onDeleteClick={onDeleteClick}
                          disabled={loading}
                        />
                      );
                    }

                    // Fallback for old-style events
                    return (
                      <EventListItem
                        key={event.eventId}
                        event={event}
                        onClick={onEventClick}
                        onDelete={onDeleteClick}
                        disabled={loading}
                      />
                    );
                  })}
                </List>
              </InfiniteScroll>
            </div>

            <Box sx={{ flexShrink: 0, mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Page:
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  onClick={() => onPageSizeChange(50)}
                  variant={pageSize === 50 ? 'contained' : 'outlined'}
                  sx={{ minWidth: 40, fontSize: '0.7rem', py: 0.25 }}
                >
                  50
                </Button>
                <Button
                  onClick={() => onPageSizeChange(100)}
                  variant={pageSize === 100 ? 'contained' : 'outlined'}
                  sx={{ minWidth: 40, fontSize: '0.7rem', py: 0.25 }}
                >
                  100
                </Button>
                <Button
                  onClick={() => onPageSizeChange(200)}
                  variant={pageSize === 200 ? 'contained' : 'outlined'}
                  sx={{ minWidth: 40, fontSize: '0.7rem', py: 0.25 }}
                >
                  200
                </Button>
                <Button
                  onClick={() => onPageSizeChange(500)}
                  variant={pageSize === 500 ? 'contained' : 'outlined'}
                  sx={{ minWidth: 40, fontSize: '0.7rem', py: 0.25 }}
                >
                  500
                </Button>
              </ButtonGroup>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
